import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuthStore } from '../../store';
import {
  Plus, Edit2, Trash2, X, ChevronRight, ChevronDown,
  FolderTree, Folder, FolderOpen, Loader2, GripVertical,
} from 'lucide-react';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminCategoriesTab() {
  const { token } = useAuthStore();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCat, setEditingCat] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [expandedIds, setExpandedIds] = useState(new Set());

  const [form, setForm] = useState({
    name: '',
    parent_id: '',
    description: '',
    sort_order: 0,
  });

  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(res.data || []);
    } catch {
      toast.error('Kategoriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  // Build tree structure
  const buildTree = (cats) => {
    const roots = cats.filter(c => !c.parent_id);
    const getChildren = (parentId) => cats.filter(c => c.parent_id === parentId);
    return { roots, getChildren };
  };

  const { roots, getChildren } = buildTree(categories);

  const toggleExpand = (id) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const openAddModal = (parentId = null) => {
    setEditingCat(null);
    setForm({ name: '', parent_id: parentId || '', description: '', sort_order: 0 });
    setShowModal(true);
  };

  const openEditModal = (cat) => {
    setEditingCat(cat);
    setForm({
      name: cat.name || '',
      parent_id: cat.parent_id || '',
      description: cat.description || '',
      sort_order: cat.sort_order || 0,
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Kategori adı zorunludur'); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        parent_id: form.parent_id || null,
        description: form.description || null,
        sort_order: parseInt(form.sort_order) || 0,
      };
      if (editingCat) {
        await axios.put(`${API_URL}/api/categories/${editingCat.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Kategori güncellendi');
      } else {
        await axios.post(`${API_URL}/api/categories`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Kategori eklendi');
      }
      setShowModal(false);
      fetchCategories();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'İşlem başarısız');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (cat) => {
    const children = getChildren(cat.id);
    const msg = children.length > 0
      ? `"${cat.name}" ve ${children.length} alt kategorisi silinecek. Emin misiniz?`
      : `"${cat.name}" kategorisini silmek istediğinize emin misiniz?`;
    if (!window.confirm(msg)) return;
    setDeleting(cat.id);
    try {
      await axios.delete(`${API_URL}/api/categories/${cat.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Kategori silindi');
      fetchCategories();
    } catch {
      toast.error('Kategori silinemedi');
    } finally {
      setDeleting(null);
    }
  };

  // Count products per category
  const getProductCount = (catName) => {
    // This is a placeholder - product_count could be enriched from backend
    return categories.find(c => c.name === catName)?.product_count || 0;
  };

  const CategoryRow = ({ cat, depth = 0 }) => {
    const children = getChildren(cat.id);
    const hasChildren = children.length > 0;
    const isExpanded = expandedIds.has(cat.id);

    return (
      <>
        <div
          className={`flex items-center gap-2 px-3 sm:px-4 py-2.5 border-b border-gray-50 hover:bg-gray-50 transition-colors ${!cat.is_active ? 'opacity-50' : ''}`}
          style={{ paddingLeft: `${depth * 24 + 12}px` }}
          data-testid={`category-row-${cat.id}`}
        >
          {/* Expand/Collapse */}
          <button
            onClick={() => hasChildren && toggleExpand(cat.id)}
            className={`p-0.5 rounded ${hasChildren ? 'hover:bg-gray-200 cursor-pointer' : 'invisible'}`}
          >
            {hasChildren ? (
              isExpanded ? <ChevronDown className="w-4 h-4 text-gray-500" /> : <ChevronRight className="w-4 h-4 text-gray-500" />
            ) : <span className="w-4 h-4" />}
          </button>

          {/* Icon */}
          {hasChildren && isExpanded ? (
            <FolderOpen className="w-4 h-4 text-yellow-500 shrink-0" />
          ) : hasChildren ? (
            <Folder className="w-4 h-4 text-yellow-500 shrink-0" />
          ) : (
            <Folder className="w-4 h-4 text-gray-400 shrink-0" />
          )}

          {/* Name */}
          <span className="flex-1 text-sm font-medium text-gray-900 truncate">{cat.name}</span>

          {/* Description */}
          <span className="hidden sm:block text-xs text-gray-400 truncate max-w-[120px]">{cat.description || ''}</span>

          {/* Actions */}
          <div className="flex items-center gap-0.5 shrink-0">
            <button onClick={() => openAddModal(cat.id)} className="p-1.5 text-green-600 hover:bg-green-50 rounded" title="Alt Kategori Ekle">
              <Plus className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => openEditModal(cat)} className="p-1.5 text-gray-500 hover:bg-gray-100 rounded" title="Düzenle">
              <Edit2 className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => handleDelete(cat)} disabled={deleting === cat.id} className="p-1.5 text-red-500 hover:bg-[#eef0f7] rounded" title="Sil">
              {deleting === cat.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>

        {/* Children */}
        {hasChildren && isExpanded && children.map(child => (
          <CategoryRow key={child.id} cat={child} depth={depth + 1} />
        ))}
      </>
    );
  };

  // Get parent options (exclude self and children for edit)
  const getParentOptions = () => {
    if (!editingCat) return categories.filter(c => !c.parent_id || true);
    // Exclude self and descendants
    const excludeIds = new Set([editingCat.id]);
    const addDescendants = (parentId) => {
      categories.filter(c => c.parent_id === parentId).forEach(c => {
        excludeIds.add(c.id);
        addDescendants(c.id);
      });
    };
    addDescendants(editingCat.id);
    return categories.filter(c => !excludeIds.has(c.id));
  };

  return (
    <div className="space-y-3 sm:space-y-4" data-testid="categories-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FolderTree className="w-5 h-5 text-gray-600" />
          <h2 className="text-lg font-semibold text-gray-900">Kategori Yönetimi</h2>
          <span className="text-xs text-gray-400">({categories.length} kategori)</span>
        </div>
        <button
          onClick={() => openAddModal()}
          className="inline-flex items-center gap-2 px-3 py-2 bg-[#1a224f] text-white rounded-lg text-sm font-medium hover:bg-[#12173a]"
          data-testid="add-category-btn"
        >
          <Plus className="w-4 h-4" />
          <span className="hidden sm:inline">Yeni Kategori</span>
        </button>
      </div>

      {/* Category Tree */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" />
          </div>
        ) : categories.length === 0 ? (
          <div className="p-8 text-center">
            <Folder className="w-10 h-10 mx-auto text-gray-300 mb-2" />
            <p className="text-sm text-gray-500">Henüz kategori eklenmemiş</p>
            <button onClick={() => openAddModal()} className="mt-3 text-sm text-[#1a224f] hover:text-[#12173a] font-medium">
              İlk Kategoriyi Ekle
            </button>
          </div>
        ) : (
          <div>
            {/* Header Row */}
            <div className="flex items-center gap-2 px-4 py-2 bg-gray-50 text-xs text-gray-500 font-medium border-b">
              <span className="w-5" />
              <span className="w-4" />
              <span className="flex-1">Kategori Adı</span>
              <span className="hidden sm:block w-[120px]">Açıklama</span>
              <span className="w-[100px] text-right">İşlemler</span>
            </div>
            {roots.map(cat => (
              <CategoryRow key={cat.id} cat={cat} depth={0} />
            ))}
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-2 sm:p-4 pb-20 sm:pb-4 bg-black/50" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="border-b border-gray-100 p-4 flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900">
                {editingCat ? 'Kategori Düzenle' : form.parent_id ? 'Alt Kategori Ekle' : 'Yeni Kategori'}
              </h3>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Kategori Adı <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value })}
                  placeholder="Örn: Dini Kitaplar"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1a224f]/20 focus:border-[#3a4680]"
                  required
                  data-testid="category-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Üst Kategori</label>
                <Select value={form.parent_id || '_none_'} onValueChange={(val) => setForm({ ...form, parent_id: val === '_none_' ? '' : val })} data-testid="category-parent-select">
                  <SelectTrigger className="w-full h-10 border-gray-200 rounded-lg text-sm bg-white" data-testid="category-parent-select">
                    <SelectValue placeholder="Ana Kategori (Üst Seviye)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none_">Ana Kategori (Üst Seviye)</SelectItem>
                    {getParentOptions().map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.parent_id ? `  └─ ${c.name}` : c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Açıklama</label>
                <input
                  type="text"
                  value={form.description}
                  onChange={e => setForm({ ...form, description: e.target.value })}
                  placeholder="Kategori açıklaması (opsiyonel)"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1a224f]/20 focus:border-[#3a4680]"
                  data-testid="category-description-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sıralama</label>
                <input
                  type="number"
                  value={form.sort_order}
                  onChange={e => setForm({ ...form, sort_order: e.target.value })}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1a224f]/20 focus:border-[#3a4680]"
                  data-testid="category-sort-input"
                />
              </div>
              <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg text-sm font-medium">İptal</button>
                <button type="submit" disabled={saving} className="px-4 py-2 bg-[#1a224f] text-white rounded-lg text-sm font-medium hover:bg-[#12173a] disabled:opacity-50 flex items-center gap-2" data-testid="save-category-btn">
                  {saving ? <><Loader2 className="w-4 h-4 animate-spin" />Kaydediliyor...</> : 'Kaydet'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
